import {
  AfterViewInit,
  Component,
  EventEmitter,
  OnInit,
  Output,
  QueryList,
  ViewChild,
  ViewChildren
} from '@angular/core';
import { FormControl, FormGroup, UntypedFormControl, UntypedFormGroup, Validators } from "@angular/forms";
import { ComponentBase } from "../../core/component-base";
import { ModalV2Component } from "../modal-v2/modal-v2.component";
import { TabulatedContentComponent } from "../tabulated-content/tabulated-content.component";
import { CreateSiteModalComponent } from "../create-site-modal/create-site-modal.component";
import { ModalViewport } from "../modal-v2/modal-viewport";
import { TabItem } from "../tabulated-content/tab-item.model";
import { optionalEmailValidator } from "../../core/validators/optional-email.validator";
import { Address } from "../input-address/address.model";
import { InputAddressComponent } from "../input-address/input-address.component";
import { PatientDetail } from "../../core/models/patient-detail.model";
import { SelectOption } from "../../core/models/select-option.model";
import { LogHelper } from "../../core/helpers/log.helper";
import { AlertService } from "../alert/alert.service";
import { PatientService } from "../../core/services/patient.service";
import { AutosuggestDropdownInputComponent } from "../autosuggest-dropdown-input/autosuggest-dropdown-input.component";
import { IPatientUpdate } from "../../core/services/interfaces/patient-update.interface";
import { PatientTrial } from "../../core/models/patient-trial.model";
import { Countries } from "../../core/constants/countries";
import { LanguageCultureOptions } from "../../core/constants/language-culture-options";
import { PatientManageTrialsComponent } from "./patient-manage-trials/patient-manage-trials.component";
import { Permissions } from "../../core/constants/permissions";
import { AuthService } from "../../core/services/auth.service";
import { Router } from "@angular/router";
import { PatientDeleteModalComponent } from "../patient-delete-modal/patient-delete-modal.component";
import { TimezoneSelectComponent } from "../timezone-select/timezone-select.component";
import { requiredNoWhitespaceValidator } from "../../core/validators/no-whitespace.validator";

@Component({
  selector: 'app-patient-edit-modal',
  templateUrl: './patient-edit-modal.component.html',
  styleUrl: './patient-edit-modal.component.scss'
})
export class PatientEditModalComponent extends ComponentBase implements OnInit, AfterViewInit {
  @ViewChildren('patientTrial', { read: PatientManageTrialsComponent }) patientTrials: QueryList<PatientManageTrialsComponent>;
  @ViewChild('modal') modal: ModalV2Component;
  @ViewChild('tabulatedContent') tabulatedContent: TabulatedContentComponent;
  @ViewChild('createSiteModal') createSideModal: CreateSiteModalComponent;
  @ViewChild('countriesSelect') countriesSelect: AutosuggestDropdownInputComponent;
  @ViewChild('homeAddress') homeAddress: InputAddressComponent;
  @ViewChild('deletePatientModal') deletePatientModal: PatientDeleteModalComponent;
  @ViewChild('timezoneSelect') timezoneSelect: TimezoneSelectComponent;

  @Output('patientUpdated') patientUpdated = new EventEmitter();

  viewPort: ModalViewport = ModalViewport.Medium;

  patientTrialForms: UntypedFormGroup[] = [];
  patient = new PatientDetail();
  hideAssignTrialButton = false;
  assignedTrialIds: string[] = [];
  hideAddress = false;
  isCreatedAfter2022 = false;
  form: FormGroup;
  patientInfoForm: FormGroup;
  extraInfoForm: FormGroup;
  patientLoaded = false;
  countries: { value: string, text: string }[] = [];
  languageOptions: SelectOption[] = [];

  tabs: TabItem[] = [
    new TabItem({title: 'Patient Info', visible: true}),
    new TabItem({title: 'Extra Info', visible: true}),
    new TabItem({title: 'Assigned Trials', visible: true})
  ];
  selectedTabIndex = 0;
  showCreatePatientButton = false;

  constructor(private _alertService: AlertService, private _patientService: PatientService, public authService: AuthService, private router: Router) {
    super();

    for (const country of Countries.all()) {
      this.countries.push({ value: country.code, text: country.name });
    }

    const languageCultures = LanguageCultureOptions.all();
    languageCultures.forEach(culture => {
      this.languageOptions.push({ value: culture.cultureInfoCode.toLowerCase(), text: culture.language + ' (' + culture.cultureInfoCode.toLowerCase() + ')' });
    });
    this.languageOptions.sort((a, b) => a.text.localeCompare(b.text));
  }

  ngOnInit() {
    this.initForm();
  }

  ngAfterViewInit(): void {

  }

  /**
   * True when there is an un-confirmed trial form on screen — i.e. a newly added trial row that
   * hasn't been confirmed via "Add Trial" yet (so patientTrialForms is longer than patient.trials),
   * or any trial form that is currently invalid. The Save button must stay disabled in this state,
   * otherwise patientTrialForms and patient.trials fall out of sync and saving corrupts the trial
   * data / throws on the un-mapped index.
   */
  get hasPendingTrialForm(): boolean {
    return this.patientTrialForms.length > this.patient.trials.length
      || this.patientTrialForms.some(form => form.invalid);
  }

  initForm(): void {
    this.patientLoaded = false;
    this.form = new FormGroup({
      processing: new FormControl<boolean>(false),
      patientInfo: new FormGroup({
        firstname: new FormControl<string>('', [requiredNoWhitespaceValidator(), Validators.maxLength(30)]),
        lastname: new FormControl<string>('', [requiredNoWhitespaceValidator(), Validators.maxLength(30)]),
        email: new FormControl<string>('', [requiredNoWhitespaceValidator(), optionalEmailValidator]),
        country: new FormControl<string>(''),
        address: new FormControl<Address>(null),
        homeAddress: new FormControl(null),
        timezone: new FormControl(''),
        timezoneLabel: new FormControl(''),
        culture: new FormControl<string>('en-gb')
      }),
      extraInfo: new FormGroup({
        notes: new FormControl<string>(''),
        policyReminders: new FormControl<string>(''),
        patientNotOnApp: new FormControl<boolean>(false, Validators.required)
      })
    });

    this.patientInfoForm = this.form.get('patientInfo') as FormGroup;
    this.extraInfoForm = this.form.get('extraInfo') as FormGroup;

    // Show exclamation mark on tabs is form is invalid
    this.form.valueChanges.subscribe(changes => {
      if (this.patientInfoForm.invalid) {
        this.tabulatedContent.addTabClass('Patient Info', 'invalid');
      } else {
        this.tabulatedContent.removeTabClass('Patient Info', 'invalid');
      }

      if (this.extraInfoForm.invalid) {
        this.tabulatedContent.addTabClass('Extra Info', 'invalid');
      } else {
        this.tabulatedContent.removeTabClass('Extra Info', 'invalid');
      }
    });
  }

  reset(): void {
    this.initForm();
  }

  /**
   * Shows the modal
   */
  show(patientId: string = null): void {
    this.reset();

    // Switch to the first tab
    this.tabulatedContent.switchTab(0, true);

    // Show modal
    this.modal.show();

    this._patientService.retrievePatientDetail(patientId).subscribe({
      next: patient => {
        console.log(patient);
        this.patient = patient;
        this.patientLoaded = true;
        this.isCreatedAfter2022 = patient.created && new Date(patient.created) > new Date(2022, 0, 1);

        this.patientInfoForm.setValue({
          firstname: patient.firstname,
          lastname: patient.lastname,
          email: patient.externalEmail ? patient.externalEmail : patient.email,
          country: patient.country,
          address: patient.address,
          homeAddress: patient.homeAddress,
          timezone: patient.timezone ?? '',
          timezoneLabel: '',
          culture: LanguageCultureOptions.cultureInfoCode(patient.culture).toLowerCase()
        });

        this.extraInfoForm.setValue({
          notes: patient.note,
          policyReminders: patient.policyReminders,
          patientNotOnApp: patient.patientNotOnApp,
        });

        if (patient.homeAddress.equals(patient.address))
          this.hideAddress = true;

        if (!patient.country && !patient.address)
          this.hideAddress = true;

        this.homeAddress.setAddress(patient.homeAddress);

        this.countriesSelect.setSelectedOption(patient.country);

        // Set the value on the timezone dropdown
        if (patient.timezone)
          this.timezoneSelect.setTimezone(patient.timezone);

        this.patientTrialForms = [];
        patient.trials.forEach(patientTrial => {
          this.assignedTrialIds.push(patientTrial.trialId);

          let trialForm = new UntypedFormGroup({
            id: new UntypedFormControl(patientTrial.id),
            patientCode: new UntypedFormControl(patientTrial.patientCode, [requiredNoWhitespaceValidator(), Validators.maxLength(16)]),
            intercashRemoteId: new UntypedFormControl(patientTrial.intercashRemoteId, [Validators.maxLength(100)]),
            intercashSanNumber: new UntypedFormControl(patientTrial.intercashSanNumber, [Validators.maxLength(100)]),
            trialId: new UntypedFormControl(patientTrial.trialId, Validators.required),
            trialCode: new UntypedFormControl(patientTrial.trialCode),
            siteId: new UntypedFormControl(patientTrial.siteId, Validators.required),
            consent: new UntypedFormControl(true, Validators.requiredTrue),
            trialIdIsUnique: new UntypedFormControl(true, Validators.requiredTrue),
            bankFeesIncurred: new UntypedFormControl(patientTrial.bankFeesIncurred),
            overspendTravel: new UntypedFormControl(patientTrial.overspendTravel),
            overspendMileage: new UntypedFormControl(patientTrial.overspendMileage),
            fixedFeePerVisit: new UntypedFormControl(patientTrial.fixedFeePerVisit),
            approvalNoReceipts: new UntypedFormControl(patientTrial.approvalNoReceipts),
            stateOnTrial: new UntypedFormControl(patientTrial.stateOnTrial),
            endingParticipationReason: new UntypedFormControl({ value: patientTrial.endingParticipationReason, disabled: patientTrial.endingParticipationReason }),
            engineSize: new UntypedFormControl(patientTrial.engineSize)
          });

          // The patient code on a third-party trial is that third party's key for the subject, so PGO must
          // not re-key it. A first-party consumer (SiteGO, StudyEngage) is PGO, so its patients stay
          // editable exactly as on a plain trial. AB#34872.
          if (!patient.patientIsPgoOwned)
            trialForm.get('patientCode').disable();

          this.patientTrialForms.push(trialForm);
        });

        // Disable old site address
        this.patientInfoForm.get('address').disable();
      },
      error: error => {
        LogHelper.log(error);
        this._alertService.showWarningAlert('Unable to load patient information!');
      }
    });

    this.patientInfoForm.get('country').valueChanges.subscribe(country => {
      if (country !== '') {
        let address = this.patientInfoForm.get('homeAddress').value;

        if (address === null) {
          address = new Address();
        }

        address.countryCode = country;
        this.homeAddress.setAddress(address);
      }
    });

    this.reset();
  }

  /**
   * When the timezone is changed we need to get the full text label of the selected timezone to show in the disabled
   * timezone input
   * @param option
   */
  onTimezoneChanged(option: { value: string, text: string }) {
    if (option)
      this.form.patchValue({timezoneLabel: option.text});
  }

  /**
   * Hides the modal
   */
  hide(): void {
    this.patient = new PatientDetail();
    this.modal.hide();
  }

  onModalClosed(): void {
    this.patient = new PatientDetail();
    this.hideAssignTrialButton = false;
  }

  onHandleTabChanged(index: number): void {
    this.selectedTabIndex = index;

    // Show the create patient button if the user has navigated to the last tab
    if (index === this.tabs.length - 1)
      this.showCreatePatientButton = true;
  }

  onTrialAdded(patientTrial: PatientTrial) {
    this.assignedTrialIds.push(patientTrial.trialId);
    this.patient.trials.push(patientTrial);
    this.hideAssignTrialButton = false;
  }

  onHandleCancelled() {
    this.hideAssignTrialButton = false;
    this.patientTrialForms.pop();
  }

  removeFromTrial(patientTrialId: string) {
    this.patient.trials = this.patient.trials.filter(pt => pt.id !== patientTrialId);
    this.patientTrialForms = this.patientTrialForms.filter(ptf => ptf.get("id").value !== patientTrialId);
  }

  onAddNewTrial() {
    this.hideAssignTrialButton = true;

    this.patientTrialForms.push(new UntypedFormGroup({
      id: new UntypedFormControl(''),
      patientCode: new UntypedFormControl('', [requiredNoWhitespaceValidator(), Validators.maxLength(16)]),
      intercashRemoteId: new UntypedFormControl(''),
      intercashSanNumber: new UntypedFormControl(''),
      trialId: new UntypedFormControl('', Validators.required),
      trialCode: new UntypedFormControl(''),
      siteId: new UntypedFormControl('', Validators.required),
      consent: new UntypedFormControl(false, Validators.requiredTrue),
      trialIdIsUnique: new UntypedFormControl(false, Validators.requiredTrue),
      bankFeesIncurred: new UntypedFormControl(false),
      overspendTravel: new UntypedFormControl(false),
      overspendMileage: new UntypedFormControl(false),
      fixedFeePerVisit: new UntypedFormControl(false),
      approvalNoReceipts: new UntypedFormControl(false),
      stateOnTrial: new UntypedFormControl("Active"),
      endingParticipationReason: new UntypedFormControl(''),
      engineSize: new UntypedFormControl('')
    }));
  }

  onFormSubmit() {
    if (this.form.valid) {
      this.form.patchValue({ processing: true });

      // Before we save, iterate through the patient trials and put the patient code and site details onto the patient trial.
      // Skip any trial form that has no corresponding patient.trials entry — that means a newly added trial row has not
      // been confirmed via "Add Trial" yet, so its data must not be saved (and indexing past the end would throw).
      let i = 0;
      this.patientTrialForms.forEach(patientTrialForm => {
        if (this.patient.trials[i] === undefined) {
          i++;
          return;
        }

        this.patient.trials[i].patientCode = patientTrialForm.get('patientCode').value;
        this.patient.trials[i].intercashRemoteId = patientTrialForm.get('intercashRemoteId').value;
        this.patient.trials[i].intercashSanNumber = patientTrialForm.get('intercashSanNumber').value;
        this.patient.trials[i].siteId = patientTrialForm.get('siteId').value;
        this.patient.trials[i].fixedFeePerVisit = patientTrialForm.get('fixedFeePerVisit').value;
        this.patient.trials[i].bankFeesIncurred = patientTrialForm.get('bankFeesIncurred').value;
        this.patient.trials[i].overspendTravel = patientTrialForm.get('overspendTravel').value;
        this.patient.trials[i].overspendMileage = patientTrialForm.get('overspendMileage').value;
        this.patient.trials[i].approvalNoReceipts = patientTrialForm.get('approvalNoReceipts').value;
        this.patient.trials[i].stateOnTrial = patientTrialForm.get('stateOnTrial').value;
        this.patient.trials[i].endingParticipationReason = patientTrialForm.get('endingParticipationReason').value;
        this.patient.trials[i].engineSize = patientTrialForm.get('engineSize').value;
        i++;
      });

      const updateDto: IPatientUpdate = {
        firstname: this.patientInfoForm.get('firstname').value,
        lastname: this.patientInfoForm.get('lastname').value,
        country: this.patientInfoForm.get('country').value,
        address: this.patientInfoForm.get('address').value,
        notes: this.extraInfoForm.get('notes').value,
        reminders: this.extraInfoForm.get('policyReminders').value,
        patientNotOnApp: this.extraInfoForm.get('patientNotOnApp').value,
        email: this.patientInfoForm.get('email').value,
        trials: this.patient.trials,
        homeAddress: this.patientInfoForm.get('homeAddress').value,
        culture: this.patientInfoForm.get('culture').value,
        timezone: this.patientInfoForm.get('timezone').value,
      };

      this._patientService.updatePatient(this.patient.id, updateDto).subscribe({
        next: () => {
          this._alertService.showSuccessAlert('Patient Updated Successfully');
          this.hide();
          this.patientUpdated.emit();
        },
        error: error => {
          LogHelper.log(error);
          this.form.patchValue({ processing: false });
          this._alertService.showWarningAlert(error.error.title ? error.error.title : 'Unable to update patient information!');
        }
      });
    }
  }

  onDeletedPatient() {
    void this.router.navigate(["/patient"]);
  }

  protected readonly Permissions = Permissions;
}
